/**
 * Document info action hooks for document state management
 *
 * These hooks dispatch document actions directly to the Zustand store via DocumentStore,
 * bypassing Redux. The DocumentStore handles:
 * 1. Injecting currentUser into actions (previously done by Redux currentUserMiddleware)
 * 2. Forwarding actions to the Zustand store's reduce() method
 *
 * The Zustand store's reduce() method notifies action listeners (ActionRecordService,
 * DocumentSaveService, PatchHandlerService) so middleware functionality is preserved.
 */
import {useCallback} from "react";

import {useFluxServices} from "injection/hooks";
import type {DocumentStore} from "modules/document/state/document";

/**
 * Hook to get the updateDescription action
 *
 * Dispatches directly to Zustand via DocumentStore, bypassing Redux.
 *
 * @returns A function to update the document description
 */
export function useUpdateDescription(): (description: string) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (description: string) => {
            documentStore.getState().updateDescription(description);
        },
        [documentStore],
    );
}

/**
 * Factory function to create document info actions for non-React contexts
 *
 * Use this in helper classes, services, or other non-React modules that need
 * to dispatch document info actions.
 *
 * @param documentStore - The DocumentStore from FluxServices
 * @returns An object with document info action methods
 */
export function createDocumentInfoActions(documentStore: DocumentStore) {
    return {
        updateDescription: (description: string) => {
            documentStore.getState().updateDescription(description);
        },
    };
}
